import React, { useEffect, useState, useReducer } from "react";
import { useParams, useHistory } from "react-router-dom";
import { 
    Container, Grid, Typography, Paper, FormControlLabel, Checkbox, 
    Alert, Divider, Box, IconButton, Tooltip 
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';

// Componentes
import CustomBreadcrumbs from "../../../../components/breadcrumbs";
import ResponseComponent from "../../../../components/responseComponent";
import UserAutocomplete from "../../../../components/merchants/subscriptions/UserAutocomplete";
import QuickCustomerModal from "../../../../components/merchants/subscriptions/QuickCustomerModal"; // <-- Importado

// Controladores
import { getPlan } from "../../../../controllers/company/subscriptions/PlansController";
import { enrollStudent } from "../../../../controllers/company/subscriptions/SubscriptionsController";
import SectionTitle from "../../../../components/sectionTitle";

const initialState = {
    payer: null,
    beneficiary: null,
    sameAsPayer: false,
    isLoading: false,
    open: false, title: '', message: '', success: false
};

function reducer(state, { field, value }) {
    return { ...state, [field]: value };
}

export default function SubscriptionEnroll() {
    const [state, setState] = useReducer(reducer, initialState);
    const [plan, setPlan] = useState(null);
    
    // Estados para el modal de creación rápida
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [targetField, setTargetField] = useState(null); // 'payer' o 'beneficiary'
    const [modalRole, setModalRole] = useState('payer'); // 'payer' o 'beneficiary'

    const { planId } = useParams(); // Obtenemos el ID de la URL
    const history = useHistory();

    const { payer, beneficiary, sameAsPayer, isLoading, open, title, message, success } = state;

    // 1. Cargar datos del Plan al inicio
    useEffect(() => {
        if (!planId) {
            alert("No se especificó un plan.");
            history.push('/subscriptions/plans');
            return;
        }
        
        const fetchPlan = async () => {
            const response = await getPlan(planId);
            if (response.success) {
                setPlan(response.data);
            } else {
                alert("Error cargando el plan.");
                history.push('/subscriptions/plans');
            }
        };
        fetchPlan();
    }, [planId, history]);

    // 2. Lógica "Mismo que el pagador"
    useEffect(() => {
        if (sameAsPayer) {
            setState({ field: 'beneficiary', value: payer });
        }
    }, [sameAsPayer, payer]);

    // --- MANEJADORES DEL MODAL DE CREACIÓN ---

    const handleOpenCreateModal = (field, role) => {
        setTargetField(field);
        setModalRole(role);
        setIsCreateModalOpen(true);
    };

    const handleCustomerCreated = (newCustomer) => {
        // 1. Seteamos el campo correspondiente con el nuevo usuario
        setState({ field: targetField, value: newCustomer });
        
        // 2. Si "Mismo que el pagador" está activo y creamos un pagador, actualizar ambos
        if (targetField === 'payer' && state.sameAsPayer) {
            setState({ field: 'beneficiary', value: newCustomer });
        }
    };

    // --- MANEJADORES DE INSCRIPCIÓN ---
    
    const handleEnroll = async () => {
        if (!payer || !beneficiary) {
            alert("Debes seleccionar un Pagador y un Beneficiario");
            return;
        }

        setState({ field: 'isLoading', value: true });

        const data = {
            plan_id: planId,
            payer_id: payer.id,
            beneficiary_id: beneficiary.id
        };

        const response = await enrollStudent(data);

        if (response.success) {
            setState({ 
                field: 'title', value: '¡Inscripción Exitosa!',
                field: 'message', value: response.message
            });
            setState({ field: 'success', value: true });
            setState({ field: 'open', value: true });
        } else {
            setState({ field: 'title', value: 'Error' });
            setState({ field: 'message', value: response.message });
            setState({ field: 'success', value: false });
            setState({ field: 'open', value: true });
        }

        setState({ field: 'isLoading', value: false });
    };

    const handleCloseResponse = () => {
        // Al terminar, volvemos al detalle del plan para ver la nueva suscripción en la tabla
        if (success) history.push(`/subscriptions/plans/detail/${planId}`);
        else setState({ field: 'open', value: false });
    };

    if (!plan) return <Typography sx={{ p: 4 }}>Cargando información del plan...</Typography>;

    return (
        <Container maxWidth="xl">
            <CustomBreadcrumbs 
                returnTo={`/subscriptions/plans/detail/${planId}`}
                crumbs={[
                    { name: 'Planes', link: '/subscriptions/plans' },
                    { name: plan.name, link: `/subscriptions/plans/${planId}` },
                    { name: 'Inscribir', link: '#' }
                ]}
            />

            <SectionTitle title='Nueva Inscripción'/>

            <Grid container spacing={3} justifyContent={'center'}>
                <Grid item xs={12} sm={12} md={8} lg={6}>
                    <Paper elevation={3} sx={{ p: 4 }}>
                        {/* RESUMEN DEL PLAN */}
                        <Alert severity="info" icon={<PersonIcon />} sx={{ mb: 4 }}>
                            <Typography color={'primary'} variant="body1" fontWeight="bold">
                                Plan Seleccionado: {plan.name}
                            </Typography>
                            <Typography variant="body1">
                                Precio: <strong>${parseFloat(plan.price).toFixed(2)}</strong> | 
                                Frecuencia: Cada {plan.interval_count} {plan.billing_interval}(s)
                            </Typography>
                        </Alert>

                        <Grid container spacing={4}>
                            
                            {/* 1. SELECCIÓN DEL PAGADOR */}
                            <Grid item xs={12} md={12}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="h6">1. Responsable de Pago</Typography>
                                    <Tooltip title="Registrar Nuevo Cliente">
                                        {/* Payer -> Role: payer (Requiere usuario) */}
                                        <IconButton onClick={() => handleOpenCreateModal('payer', 'payer')} color="primary" size="small">
                                            <AddIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="subtitle1" color="textSecondary" paragraph>
                                    Quien recibirá los cobros y facturas (Ej. Padre/Representante).
                                </Typography>
                                
                                <UserAutocomplete
                                    label="Buscar Pagador (Nombre/Email)"
                                    onSelect={(user) => setState({ field: 'payer', value: user })}
                                    preSelectedValue={payer}
                                />
                            </Grid>

                            {/* 2. SELECCIÓN DEL BENEFICIARIO */}
                            <Grid item xs={12} md={12}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="h6">2. Beneficiario</Typography>
                                    <Tooltip title="Registrar Beneficiario">
                                        {/* Beneficiary -> Role: beneficiary (Usuario opcional) */}
                                        <IconButton 
                                            onClick={() => handleOpenCreateModal('beneficiary', 'beneficiary')} 
                                            color="primary" 
                                            size="small"
                                            disabled={sameAsPayer} // No permitir crear si es el mismo
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="subtitle1" color="textSecondary" paragraph>
                                    Quien recibirá el servicio o asistirá a las clases.
                                </Typography>
                                
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={sameAsPayer}
                                            onChange={(e) => setState({ field: 'sameAsPayer', value: e.target.checked })}
                                            disabled={!payer}
                                        />
                                    }
                                    label="Es la misma persona"
                                    sx={{ mb: 1 }}
                                />

                                <UserAutocomplete
                                    label="Buscar Beneficiario"
                                    onSelect={(user) => setState({ field: 'beneficiary', value: user })}
                                    preSelectedValue={beneficiary}
                                    disabled={sameAsPayer} 
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                            </Grid>

                            {/* RESUMEN FINAL */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f9f9f9', p: 2, borderRadius: 1 }}>
                                    <Typography variant="body1" fontWeight="bold">
                                        Total a cobrar hoy:
                                    </Typography>
                                    <Typography variant="h4" color="primary" fontWeight="bold">
                                        ${parseFloat(plan.price).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <LoadingButton
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    loading={isLoading}
                                    onClick={handleEnroll}
                                    disabled={!payer || !beneficiary}
                                    sx={{ mt: 2 }}
                                >
                                    Confirmar Inscripción y Generar Cobro
                                </LoadingButton>
                            </Grid>

                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            

            <ResponseComponent
                show={open}
                success={success}
                title={title}
                message={message}
                actionText="Aceptar"
                onClick={handleCloseResponse}
            />

            {/* --- MODAL DE CREACIÓN RÁPIDA --- */}
            <QuickCustomerModal 
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCustomerCreated={handleCustomerCreated}
                role={modalRole} // Pasamos el rol dinámico
            />
        </Container>
    );
}